/**
 * Engagement par geste : suit la main (vue ? pince ? depuis combien de temps ?) pour
 * l'indicateur, et signale UNE prise quand le pincement pouce-index tient 0,3 s,
 * n'importe où dans l'image.
 */
import { DEFAULT_PINCH_OPTIONS, pinchRatio } from './pinchDetector';
import type { Point3 } from './types';

export const PINCH_HOLD_MS = 300;

export interface PinchStatus {
  /** Main vue sur cette image. */
  readonly hand: boolean;
  readonly pinching: boolean;
  /** 0 → 1 pendant le maintien du pincement. */
  readonly progress: number;
  /** Prise confirmée sur cette image (une seule fois par pincement). */
  readonly grabbed: boolean;
}

export interface PinchWatcher {
  push(t: number, landmarks: readonly Point3[] | null): PinchStatus;
  reset(): void;
}

export function createPinchWatcher(): PinchWatcher {
  const { enter, exit, maxGapMs } = DEFAULT_PINCH_OPTIONS;
  let since: number | null = null;
  let lastPinch = -Infinity;
  let fired = false;

  const release = (): void => {
    since = null;
    fired = false;
  };
  const progress = (t: number): number =>
    since === null ? 0 : Math.min(1, (t - since) / PINCH_HOLD_MS);

  return {
    push(t, landmarks) {
      const ratio = landmarks ? pinchRatio(landmarks) : null;
      if (ratio === null) {
        // Main perdue un instant : le pincement en cours est toléré jusqu'à maxGapMs.
        if (since !== null && t - lastPinch > maxGapMs) release();
        return {
          hand: false,
          pinching: since !== null,
          progress: progress(lastPinch),
          grabbed: false,
        };
      }
      // Hystérésis : on entre sous `enter`, on ne relâche qu'au-dessus de `exit`.
      if (ratio < enter || (since !== null && ratio < exit)) {
        since ??= t;
        lastPinch = t;
        const grabbed = !fired && t - since >= PINCH_HOLD_MS;
        if (grabbed) fired = true;
        return { hand: true, pinching: true, progress: progress(t), grabbed };
      }
      release();
      return { hand: true, pinching: false, progress: 0, grabbed: false };
    },
    reset: release,
  };
}
